import { Controller } from '@hotwired/stimulus'
import { Turbo } from '@hotwired/turbo-rails'

export default class extends Controller {
  static targets = ['container', 'card', 'poster', 'embedSlot', 'sentinel', 'modal', 'commentsList', 'commentForm', 'volumeBar', 'muteBtn']

  connect () {
    this.activeCard = null
    this.currentCommentVideoId = null
    this.muted = false
    this.volume = 80
    this.playerReady = false
    this.audioUnlocked = false
    this.lastVolumeLevel = null
    this.scrollSyncPending = false
    this.volumeApplyTimers = []

    this.cardObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.dataset.intersectionRatio = String(entry.intersectionRatio)
        })
        this.scheduleScrollSync()
      },
      {
        root: this.hasContainerTarget ? this.containerTarget : null,
        threshold: [0, 0.25, 0.5, 0.65, 0.85, 1]
      }
    )

    this.onTurboRender = () => {
      requestAnimationFrame(() => {
        this.observeCards()
        if (this.hasSentinelTarget) {
          this.sentinelObserver?.disconnect()
          this.observeSentinel()
        }
        this.scheduleScrollSync()
      })
    }

    this.onPlayerMessage = (event) => this.handlePlayerMessage(event)

    document.addEventListener('turbo:render', this.onTurboRender)
    window.addEventListener('message', this.onPlayerMessage)
    this.observeCards()
    this.observeSentinel()
    this.scrollToInitialVideo()

    requestAnimationFrame(() => this.scheduleScrollSync())
  }

  disconnect () {
    this.clearReadyTimeout()
    this.cardObserver?.disconnect()
    this.sentinelObserver?.disconnect()
    this.clearVolumeApplyTimers()
    document.removeEventListener('turbo:render', this.onTurboRender)
    window.removeEventListener('message', this.onPlayerMessage)
  }

  observeCards () {
    this.cardTargets.forEach((card) => this.cardObserver.observe(card))
  }

  observeSentinel () {
    if (!this.hasSentinelTarget) return

    this.sentinelObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return

        const nextPage = entry.target.dataset.nextPage
        if (!nextPage) return

        this.loadNextPage(nextPage, entry.target)
      })
    }, { root: this.hasContainerTarget ? this.containerTarget : null, rootMargin: '240px' })

    this.sentinelObserver.observe(this.sentinelTarget)
  }

  scheduleScrollSync () {
    if (this.scrollSyncPending) return
    this.scrollSyncPending = true

    requestAnimationFrame(() => {
      this.scrollSyncPending = false
      this.syncActiveCardFromScroll()
    })
  }

  syncActiveCardFromScroll () {
    let bestCard = null
    let bestRatio = 0

    this.cardTargets.forEach((card) => {
      if (card.classList.contains('is-unavailable')) return
      const ratio = parseFloat(card.dataset.intersectionRatio || '0')
      if (ratio > bestRatio) {
        bestRatio = ratio
        bestCard = card
      }
    })

    if (bestCard && bestRatio >= 0.55) {
      if (this.activeCard !== bestCard) {
        if (this.activeCard) this.deactivatePlayer(this.activeCard)
        this.playCard(bestCard)
      }
      return
    }

    if (bestRatio < 0.2 && this.activeCard) {
      this.deactivatePlayer(this.activeCard)
    }
  }

  handlePlayerMessage (event) {
    const data = event.data
    if (!data || data['x-tiktok-player'] !== true) return

    if (data.type === 'onError') {
      if (this.activeCard) this.handleUnavailableEmbed(this.activeCard, 'tiktok_error')
      return
    }

    if (data.type === 'onPlayerReady') {
      this.playerReady = true
      this.clearReadyTimeout()
      this.applyVolumeForCard(this.activeCard, true)
      return
    }

    if (data.type === 'onStateChange' && data.value === 1) {
      if (!this.playerReady) this.playerReady = true
      this.clearReadyTimeout()
      this.applyVolumeForCard(this.activeCard, true)
      return
    }

    if (data.type === 'onVolumeChange' && typeof data.value === 'number') {
      return
    }

    if (data.type !== 'onStateChange' || data.value !== 0 || !this.activeCard) return

    this.replayActivePlayer()
  }

  activatePlayer (event) {
    event.preventDefault()
    event.stopPropagation()

    const card = event.currentTarget.closest('[data-video-feed-target="card"]')
    this.playCard(card)
  }

  playCard (card) {
    if (!card) return
    if (card.classList.contains('is-unavailable')) return

    if (card.classList.contains('is-paused')) {
      this.resumePlayer(card)
      return
    }

    if (card.classList.contains('is-playing')) return

    const poster = card.querySelector('[data-video-feed-target="poster"]')
    const slot = card.querySelector('[data-video-feed-target="embedSlot"]')
    const src = card.dataset.embedSrc
    if (!slot || !src) return

    let iframe = slot.querySelector('iframe')
    if (!iframe) {
      iframe = document.createElement('iframe')
      iframe.setAttribute('title', 'Shoppable video')
      iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen')
      iframe.className = 'feed-embed-frame'
      slot.querySelector('.feed-embed-shell')?.appendChild(iframe)
    }

    this.playerReady = false
    this.lastVolumeLevel = null
    this.activeIframe = iframe
    iframe.src = src
    poster?.classList.add('is-hidden')
    slot.hidden = false
    card.classList.remove('is-paused')
    card.classList.add('is-playing')
    this.activeCard = card
    this.element.classList.add('is-playing')

    this.scheduleVolumeApply(card)
    if (card.dataset.platform === 'tiktok') {
      this.armReadyTimeout(card)
    } else if (card.dataset.platform === 'instagram') {
      // Instagram embeds do not post ready events; treat iframe load as playable.
      iframe.addEventListener('load', () => {
        if (this.activeCard === card) this.playerReady = true
      }, { once: true })
    }
  }

  togglePlayback (event) {
    event.preventDefault()
    event.stopPropagation()

    const card = event.currentTarget.closest('[data-video-feed-target="card"]')
    if (!card) return

    if (card.classList.contains('is-paused')) {
      this.resumePlayer(card)
      return
    }

    if (card.classList.contains('is-playing')) {
      this.pausePlayer(card)
    }
  }

  pausePlayer (card) {
    if (!card || !card.classList.contains('is-playing')) return

    const iframe = card.querySelector('iframe')
    this.pauseEmbeddedPlayer(iframe, card.dataset.platform)

    card.classList.remove('is-playing')
    card.classList.add('is-paused')
    this.element.classList.remove('is-playing')
  }

  resumePlayer (card) {
    if (!card || !card.classList.contains('is-paused')) return

    const iframe = card.querySelector('iframe')
    this.activeCard = card
    this.activeIframe = iframe
    this.playEmbeddedPlayer(iframe, card.dataset.platform)

    card.classList.remove('is-paused')
    card.classList.add('is-playing')
    this.element.classList.add('is-playing')
    this.applyVolumeForCard(card, true)
  }

  closePlayer (event) {
    event.preventDefault()
    event.stopPropagation()

    const card = event.currentTarget.closest('[data-video-feed-target="card"]')
    this.deactivatePlayer(card)
  }

  deactivatePlayer (event) {
    const card = event?.currentTarget
      ? event.currentTarget.closest('[data-video-feed-target="card"]')
      : event

    if (!card) return

    this.clearReadyTimeout()

    const poster = card.querySelector('[data-video-feed-target="poster"]')
    const slot = card.querySelector('[data-video-feed-target="embedSlot"]')
    const iframe = slot?.querySelector('iframe')

    this.clearVolumeApplyTimers()
    this.pauseEmbeddedPlayer(iframe, card.dataset.platform)

    if (iframe) {
      window.setTimeout(() => {
        iframe.removeAttribute('src')
      }, 120)
    }

    if (slot) slot.hidden = true
    poster?.classList.remove('is-hidden')
    card.classList.remove('is-playing', 'is-paused')

    if (this.activeCard === card) {
      this.activeCard = null
      this.activeIframe = null
      this.playerReady = false
      this.lastVolumeLevel = null
      this.element.classList.remove('is-playing')
    }
  }

  replayActivePlayer () {
    const iframe = this.activeCard?.querySelector('iframe')
    if (!iframe?.contentWindow) return

    const platform = this.activeCard.dataset.platform
    if (platform !== 'tiktok') {
      iframe.src = iframe.src
      return
    }

    iframe.contentWindow.postMessage({ type: 'seekTo', value: 0, 'x-tiktok-player': true }, '*')
    window.setTimeout(() => {
      iframe.contentWindow?.postMessage({ type: 'play', 'x-tiktok-player': true }, '*')
      this.applyVolumeForCard(this.activeCard, true)
    }, 120)
  }

  pauseEmbeddedPlayer (iframe, platform) {
    if (!iframe?.contentWindow) return

    if (platform === 'tiktok') {
      iframe.contentWindow.postMessage({ type: 'pause', 'x-tiktok-player': true }, '*')
      return
    }

    iframe.contentWindow.postMessage({ type: 'pause', value: undefined }, '*')
  }

  playEmbeddedPlayer (iframe, platform) {
    if (!iframe?.contentWindow) return

    if (platform === 'tiktok') {
      iframe.contentWindow.postMessage({ type: 'play', 'x-tiktok-player': true }, '*')
      return
    }

    iframe.contentWindow.postMessage({ type: 'play', value: undefined }, '*')
  }

  toggleMute (event) {
    event.preventDefault()
    event.stopPropagation()

    this.audioUnlocked = true
    const card = this.activeCard
    if (!card) return

    this.muted = !this.muted
    if (!this.muted) this.volume = 80
    this.updateVolumeUI()
    this.applyVolumeForCard(card, true)
  }

  scheduleVolumeApply (card) {
    this.clearVolumeApplyTimers()
    ;[250, 700, 1400, 2400].forEach((delay) => {
      const timer = window.setTimeout(() => this.applyVolumeForCard(card, true), delay)
      this.volumeApplyTimers.push(timer)
    })
  }

  clearVolumeApplyTimers () {
    this.volumeApplyTimers.forEach((timer) => window.clearTimeout(timer))
    this.volumeApplyTimers = []
  }

  sendPlayerMessage (iframe, type, value) {
    if (!iframe?.contentWindow) return false

    const message = { type, 'x-tiktok-player': true }
    if (value !== undefined) message.value = Number(value)

    iframe.contentWindow.postMessage(message, '*')
    return true
  }

  applyVolumeForCard (card, force = false) {
    if (!card || card.dataset.platform !== 'tiktok') return
    if (!force && !this.playerReady) return

    const iframe = card.querySelector('iframe')
    if (!iframe?.contentWindow) return

    const level = this.muted ? 0 : 80
    if (this.lastVolumeLevel === level && !force) return

    this.lastVolumeLevel = level
    this.volume = level

    if (level === 0) {
      this.sendPlayerMessage(iframe, 'mute')
      this.sendPlayerMessage(iframe, 'changeVolume', 0)
      return
    }

    this.sendPlayerMessage(iframe, 'unMute')
    this.sendPlayerMessage(iframe, 'changeVolume', level)
  }

  updateVolumeUI () {
    const muted = !!this.muted

    if (this.hasVolumeBarTarget) this.volumeBarTarget.dataset.muted = muted ? 'true' : 'false'
    if (this.hasMuteBtnTarget) {
      this.muteBtnTarget.dataset.muted = muted ? 'true' : 'false'
      this.muteBtnTarget.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video')
    }
  }

  async loadNextPage (page, sentinel) {
    if (sentinel.dataset.loading === 'true') return
    sentinel.dataset.loading = 'true'

    try {
      const response = await fetch(`/feed?page=${page}`, {
        headers: { Accept: 'text/vnd.turbo-stream.html' }
      })
      if (!response.ok) throw new Error('Failed to load more videos')

      Turbo.renderStreamMessage(await response.text())
      sentinel.remove()
    } catch (error) {
      sentinel.dataset.loading = 'false'
      if (process.env.NODE_ENV !== 'production') console.error(error)
    }
  }

  scrollToInitialVideo () {
    if (!window.location.hash) return

    const target = document.querySelector(window.location.hash)
    if (!target || !this.hasContainerTarget) return

    requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: 'instant', block: 'start' })
    })
  }

  openComments (event) {
    event.stopPropagation()
    const videoId = event.currentTarget.dataset.videoId
    if (!videoId) return

    this.currentCommentVideoId = videoId
    if (!this.hasModalTarget) return

    this.modalTarget.classList.add('is-open')
    this.modalTarget.setAttribute('aria-hidden', 'false')

    fetch(`/feed/videos/${videoId}/comments`, { headers: { Accept: 'text/html' } })
      .then((response) => response.text())
      .then((html) => {
        if (this.hasCommentsListTarget) this.commentsListTarget.innerHTML = html
      })
  }

  closeComments (event) {
    if (event.target !== event.currentTarget) return
    this.hideCommentsModal()
  }

  hideCommentsModal () {
    if (!this.hasModalTarget) return

    this.modalTarget.classList.remove('is-open')
    this.modalTarget.setAttribute('aria-hidden', 'true')
    if (this.hasCommentsListTarget) this.commentsListTarget.innerHTML = ''
    if (this.hasCommentFormTarget) this.commentFormTarget.reset()
  }

  submitComment (event) {
    event.preventDefault()

    const form = event.currentTarget
    const bodyInput = form.querySelector('[name="body"]')
    const parentInput = form.querySelector('[name="parent_id"]')
    const body = bodyInput?.value.trim()
    if (!body || !this.currentCommentVideoId) return

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const payload = new URLSearchParams({ body })
    if (parentInput?.value) payload.set('parent_id', parentInput.value)

    fetch(`/feed/videos/${this.currentCommentVideoId}/comments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-CSRF-Token': token,
        Accept: 'text/vnd.turbo-stream.html'
      },
      body: payload.toString()
    }).then(async (response) => {
      if (!response.ok) return

      const stream = await response.text()
      if (stream) Turbo.renderStreamMessage(stream)

      if (form.classList.contains('feed-comment-reply-form')) {
        form.hidden = true
        form.reset()
        return
      }

      if (this.hasCommentFormTarget) this.commentFormTarget.reset()
    })
  }

  armReadyTimeout (card) {
    this.clearReadyTimeout()
    // TikTok player posts onPlayerReady / playing state. If neither arrives, source is likely gone.
    this.readyTimeout = window.setTimeout(() => {
      if (this.activeCard !== card || this.playerReady) return
      if (card.classList.contains('is-unavailable')) return
      this.handleUnavailableEmbed(card, 'ready_timeout')
    }, 10000)
  }

  clearReadyTimeout () {
    if (this.readyTimeout) {
      window.clearTimeout(this.readyTimeout)
      this.readyTimeout = null
    }
  }

  handleUnavailableEmbed (card, reason) {
    if (!card || card.classList.contains('is-unavailable')) return

    this.clearReadyTimeout()
    this.deactivatePlayer(card)
    card.classList.add('is-unavailable')

    const notice = card.querySelector('[data-feed-unavailable]')
    if (notice) notice.hidden = false

    this.reportUnavailable(card.dataset.videoId, reason)
    this.advancePastCard(card)
  }

  advancePastCard (card) {
    const cards = this.cardTargets
    const index = cards.indexOf(card)
    const next = cards.slice(index + 1).find((candidate) => !candidate.classList.contains('is-unavailable'))
    if (!next) return

    window.setTimeout(() => {
      next.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      this.playCard(next)
    }, 450)
  }

  reportUnavailable (videoId, reason) {
    if (!videoId || this.reportedUnavailable?.has(videoId)) return
    this.reportedUnavailable = this.reportedUnavailable || new Set()
    this.reportedUnavailable.add(videoId)

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(`/feed/videos/${videoId}/report_unavailable`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': token || '',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ reason: reason || 'client' }),
      credentials: 'same-origin'
    }).catch(() => {})
  }
}
